import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as yup from "yup";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import ExternalLink from "../../../../components/ExternalLink";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import harmony_step_1 from "./images/Harmony_step_1.png";
import harmony_step_2 from "./images/Harmony_step_2.png";
import harmony_step_3 from "./images/Harmony_step_3.png";
import harmony_step_4 from "./images/Harmony_step_4.png";

const CheckpointHarmonyBrowseIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const handleClickShowSecret = () => setShowSecret(!showSecret);
  const handleMouseDownSecret = () => setShowSecret(!showSecret);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const formInitialValues = {
    clientID: "",
    clientSecret: "",
    authenticationURL: "",
  };
  const formCheckoutSchema = yup.object().shape({
    clientID: yup.string().required("required"),
    clientSecret: yup.string().required("required"),
    authenticationURL: yup.string().required("required"),
  });
  const handleFormSubmit = (values) => {
    onSubmit(values);
  };

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated(
    "checkpoint_harmony_browse"
  );
  const [loading, setLoading] = useState(false);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerCheckpointHarmonyBrowseIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerCheckpointHarmonyBrowseIntegration(
      values.clientID,
      values.clientSecret,
      values.authenticationURL
    )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Checkpoint Harmony Browse integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated && !loading && !isIntegratedLoading
            ? "Once integrated, Breeze will read configurations from Checkpoint Harmony Browse and prioritize remediations."
            : ""
        }
        image={logos.CheckpointHarmonyLogo}
        name={"Checkpoint Harmony Browse"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>
                Checkpoint Harmony Browse is integrated!
              </Typography>
            </Box>
          ) : (
            <div>
              <Box mb="20px">
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    p: 2,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 1,
                  }}
                >
                  <strong>Note:</strong> Harmony Browse is analyzed via Harmony
                  Endpoint integration.
                </Typography>

                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Formik
                  onSubmit={handleFormSubmit}
                  initialValues={formInitialValues}
                  validationSchema={formCheckoutSchema}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <GuidelineStepByStep>
                        <GuidelineStep
                          title={
                            <ExternalLink
                              href="https://portal.checkpoint.com/signin"
                              text="Login to the "
                              linkText="Infinity Portal"
                              sx={{ fontSize: "20px", fontWeight: "600" }}
                            />
                          }
                        ></GuidelineStep>
                        <GuidelineStep title="Click on Settings > API Keys">
                          <ScreenshotAccordion
                            images={[
                              {
                                file: harmony_step_1,
                                altext: "Harmony API Keys page",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                        </GuidelineStep>
                        <GuidelineStep title="Generate a new account API key">
                          <Typography variant={"body2"}>
                            Click on "New" and select "New account API Key"
                          </Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: harmony_step_2,
                                altext: "Generate new account API key",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                        </GuidelineStep>
                        <GuidelineStep title="Fill in the details, then click Create">
                          <Typography variant={"body2"}>
                            Service: <strong>Endpoint</strong>
                          </Typography>
                          <Typography variant={"body2"}>
                            Expiration: <strong>At least 2 year</strong>
                          </Typography>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant={"body2"}>
                              Description:{" "}
                              <strong>Harmony Browse Key - Breeze</strong>
                            </Typography>
                            <CustomCopyButton
                              copyTitle={"description"}
                              copyText={"Harmony Browse Key - Breeze"}
                            />
                          </Box>
                          <Typography variant={"body2"}>
                            Roles: <strong>Read-Only</strong>
                          </Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: harmony_step_3,
                                altext: "New account API key details",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                        </GuidelineStep>
                        <GuidelineStep title="Paste here your Checkpoint Harmony Browse Client ID, API key and authentication URL:">
                          <ScreenshotAccordion
                            images={[
                              {
                                file: harmony_step_4,
                                altext: "API key details",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                          <Grid item>
                            <TextField
                              fullWidth
                              variant="filled"
                              name="clientID"
                              label="Client ID"
                              value={values.clientID}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={!!touched.clientID && !!errors.clientID}
                              helperText={touched.clientID && errors.clientID}
                              disabled={guideMode}
                            />
                          </Grid>
                          <Box display="grid" mb="20px" gap="10px">
                            <TextField
                              fullWidth
                              variant="filled"
                              type={showSecret ? "text" : "password"}
                              name="clientSecret"
                              autoComplete="off"
                              label="Client Secret"
                              value={values.clientSecret}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={
                                !!touched.clientSecret && !!errors.clientSecret
                              }
                              helperText={
                                touched.clientSecret && errors.clientSecret
                              }
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label="toggle secret visibility"
                                      onClick={handleClickShowSecret}
                                      onMouseDown={handleMouseDownSecret}
                                    >
                                      {showSecret ? (
                                        <Visibility />
                                      ) : (
                                        <VisibilityOff />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                              disabled={guideMode}
                            />
                          </Box>
                          <Grid item>
                            <TextField
                              fullWidth
                              variant="filled"
                              name="authenticationURL"
                              label="Authentication URL"
                              value={values.authenticationURL}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={
                                !!touched.authenticationURL &&
                                !!errors.authenticationURL
                              }
                              helperText={
                                touched.authenticationURL &&
                                errors.authenticationURL
                              }
                              disabled={guideMode}
                            />
                          </Grid>

                          <Box display="flex" justifyContent="center">
                            <PrimaryButton type="submit" disabled={guideMode}>
                              Connect
                            </PrimaryButton>
                          </Box>
                        </GuidelineStep>
                      </GuidelineStepByStep>
                    </Form>
                  )}
                </Formik>
              </Box>
            </div>
          )
        }
      />
    </Box>
  );
};

export default CheckpointHarmonyBrowseIntegration;
